export type DatesAvailabilityItem = {
  id?: string | number;
  dates?: string;
  availability?: string;
  spacesAvailable?: string;
  limitedAvailability?: string;
};

export type TrekPackageRow = {
  id?: string | number;
  trek_title?: string;
  trek_route?: string;
  trek_type?: string;
  trek_direction?: string;
  trek_link?: string;
  trek_details?: string;
  trek_image?: { url?: string; alt?: string; width?: number; height?: number };
};

export type TrekPriceRow = {
  id?: string | number;
  trek_route?: string;
  trek_title?: string;
  trek_price?: string;
  trek_person_day?: string;
  availability?: string;
  /** HTML snippet for flexible-date packages (e.g. self-guided "To suit you"). */
  dates?: string;
  datesAvailability?: DatesAvailabilityItem[] | Record<string, DatesAvailabilityItem> | null;
};

/** Strip tags from Kirby `dates` HTML for compact UI labels. */
export function plainDatesLabel(html?: string | null): string {
  return String(html ?? "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export type TrekRouteVariant = "full" | "westerly" | "easterly";

export function normalizeTrekString(str: string | null | undefined): string {
  return String(str ?? "")
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "");
}

export function matchesTrekTitle(
  trekTitle: string | null | undefined,
  trekRoute: string | null | undefined,
): boolean {
  const normalizedTrekTitle = normalizeTrekString(trekTitle);
  if (!normalizedTrekTitle) return false;
  const normalizedRoute = normalizeTrekString(trekRoute);
  if (!normalizedRoute) {
    return (
      normalizedTrekTitle.includes("tour du mont blanc") ||
      normalizedTrekTitle === "tmb"
    );
  }
  return (
    normalizedRoute === normalizedTrekTitle ||
    normalizedTrekTitle.includes(normalizedRoute) ||
    normalizedRoute.includes(normalizedTrekTitle)
  );
}

export function filterTrekRowsByTitle<T extends { trek_route?: string }>(
  rows: T[] | null | undefined,
  trekTitle: string | null | undefined,
): T[] {
  if (!rows?.length) return [];
  return rows.filter((row) => matchesTrekTitle(trekTitle, row.trek_route));
}

export function toDatesArray(
  raw: TrekPriceRow["datesAvailability"],
): DatesAvailabilityItem[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return Object.keys(raw)
    .filter((k) => /^\d+$/.test(k))
    .map((k) => raw[k])
    .filter((x): x is DatesAvailabilityItem => x != null);
}

export function hasNoAvailability(availability?: string | null): boolean {
  return (
    normalizeTrekString(availability) === "no availability" ||
    normalizeTrekString(availability) === "fully booked"
  );
}

function isBookableDate(item: DatesAvailabilityItem): boolean {
  const availability = normalizeTrekString(item.availability);
  if (
    availability === "fully booked" ||
    availability === "no availability"
  ) {
    return false;
  }
  return true;
}

export function findNextAvailableDate(
  datesAvailability: TrekPriceRow["datesAvailability"],
): DatesAvailabilityItem | null {
  const dates = toDatesArray(datesAvailability);
  return dates.find((item) => item.dates && isBookableDate(item)) ?? null;
}

function scoreFlagshipPackage(pkg: TrekPackageRow): number {
  const title = normalizeTrekString(pkg.trek_title);
  let score = 0;
  if (title.includes("full self-guided")) score += 100;
  else if (title.includes("self-guided") && title.includes("full")) score += 90;
  else if (title.includes("full") && title.includes("guided")) score += 70;
  else if (title.includes("self-guided")) score += 50;
  else if (title.includes("full")) score += 40;
  else if (title.includes("guided")) score += 20;
  return score;
}

export function pickFlagshipPackage(
  packages: TrekPackageRow[],
): TrekPackageRow | null {
  if (!packages.length) return null;
  return [...packages].sort(
    (a, b) => scoreFlagshipPackage(b) - scoreFlagshipPackage(a),
  )[0] ?? null;
}

function packageIndex(
  packages: TrekPackageRow[],
  pkg: TrekPackageRow | null,
): number {
  if (!pkg) return -1;
  if (pkg.id != null && pkg.id !== "") {
    const wanted = String(pkg.id);
    const byId = packages.findIndex(
      (item) => item.id != null && String(item.id) === wanted,
    );
    if (byId >= 0) return byId;
  }
  return packages.findIndex(
    (item) =>
      normalizeTrekString(item.trek_title) ===
      normalizeTrekString(pkg.trek_title),
  );
}

export function findPriceForPackage(
  prices: TrekPriceRow[],
  pkg: TrekPackageRow | null,
  packages: TrekPackageRow[],
): TrekPriceRow | null {
  if (!pkg || !prices.length) return null;
  // Kirby structure ids can be string or number depending on template.
  if (pkg.id != null && pkg.id !== "") {
    const wanted = String(pkg.id);
    const byId = prices.find(
      (row) => row.id != null && String(row.id) === wanted,
    );
    if (byId) return byId;
  }
  const index = packageIndex(packages, pkg);
  if (index >= 0 && prices[index]) return prices[index] ?? null;
  return (
    prices.find(
      (row) =>
        normalizeTrekString(row.trek_title) ===
        normalizeTrekString(pkg.trek_title),
    ) ?? null
  );
}

export type TrekPackageSummary = {
  package: TrekPackageRow;
  price: TrekPriceRow | null;
  yearLabel: string;
  fromPrice: string;
  personDay: string;
  availability: string;
  nextDate: string;
  spacesLabel: string;
  itineraryPath: string;
};

export function buildTrekPackageSummary(options: {
  packages: TrekPackageRow[];
  prices1: TrekPriceRow[];
  prices2: TrekPriceRow[];
  year1Label?: string;
  year2Label?: string;
  /** When set, summarise this package instead of the flagship pick. */
  preferredPackage?: TrekPackageRow | null;
}): TrekPackageSummary | null {
  const pkg =
    options.preferredPackage ?? pickFlagshipPackage(options.packages);
  if (!pkg) return null;

  const price2 = findPriceForPackage(options.prices2, pkg, options.packages);
  const price1 = findPriceForPackage(options.prices1, pkg, options.packages);

  const preferYear2 =
    price2 &&
    (String(price2.trek_price ?? "").trim() ||
      toDatesArray(price2.datesAvailability).length > 0 ||
      String(price2.availability ?? "").trim());

  const price = preferYear2 ? price2 : price1;
  const yearLabel = preferYear2
    ? String(options.year2Label ?? "").trim()
    : String(options.year1Label ?? "").trim();

  const next = findNextAvailableDate(price?.datesAvailability);
  let spacesLabel = "";
  if (next?.spacesAvailable) {
    spacesLabel = `${next.spacesAvailable} space${next.spacesAvailable === "1" ? "" : "s"} available`;
  } else if (next?.limitedAvailability) {
    spacesLabel = `${next.limitedAvailability} space${next.limitedAvailability === "1" ? "" : "s"} available`;
  }

  const link =
    typeof pkg.trek_link === "string" ? pkg.trek_link.trim() : "";

  // Fixed departures first; self-guided often only has the HTML `dates` field.
  const nextDate =
    String(next?.dates ?? "").trim() || plainDatesLabel(price?.dates);

  return {
    package: pkg,
    price: price ?? null,
    yearLabel,
    fromPrice: String(price?.trek_price ?? "").trim(),
    personDay: String(price?.trek_person_day ?? "").trim(),
    availability: String(price?.availability ?? "").trim(),
    nextDate,
    spacesLabel,
    itineraryPath: link,
  };
}

export function matchPackageToRouteVariant(
  packages: TrekPackageRow[],
  variant: TrekRouteVariant,
): TrekPackageRow | null {
  if (!packages.length) return null;

  const scored = packages.map((pkg) => {
    const title = normalizeTrekString(pkg.trek_title);
    const direction = normalizeTrekString(pkg.trek_direction);
    const haystack = `${title} ${direction}`;
    const isSelfGuided = haystack.includes("self-guided");
    const isGuided = haystack.includes("guided") && !isSelfGuided;
    let score = 0;

    if (variant === "full") {
      // Match Trek Packages: full circuit availability is the Full Guided trek
      // (fixed departures). Self-guided is flexible and has empty datesAvailability.
      if (haystack.includes("full")) score += 50;
      if (isGuided && haystack.includes("full")) score += 40;
      if (isSelfGuided && haystack.includes("full")) score += 20;
      if (!haystack.includes("west") && !haystack.includes("east")) score += 10;
      if (haystack.includes("west") || haystack.includes("east")) score -= 40;
      if (haystack.includes("refuge")) score -= 30;
      if (haystack.includes("best of")) score -= 25;
    } else if (variant === "westerly") {
      if (haystack.includes("westerly") || haystack.includes("west"))
        score += 80;
      if (isGuided) score += 30;
      if (isSelfGuided) score -= 15;
      if (
        haystack.includes("anti-clockwise") ||
        haystack.includes("anticlockwise")
      ) {
        score += 20;
      }
      if (haystack.includes("east")) score -= 50;
    } else if (variant === "easterly") {
      if (haystack.includes("easterly") || haystack.includes("east"))
        score += 80;
      if (isGuided) score += 30;
      if (isSelfGuided) score -= 15;
      if (haystack.includes("clockwise") && !haystack.includes("anti"))
        score += 20;
      if (haystack.includes("west")) score -= 50;
    }

    return { pkg, score };
  });

  scored.sort((a, b) => b.score - a.score);
  const best = scored[0];
  if (!best || best.score <= 0) {
    return variant === "full" ? pickFlagshipPackage(packages) : null;
  }
  return best.pkg;
}

/**
 * Summarise packages/prices for a trek title from the current page payload.
 */
export function useTrekPackageSummary(trekTitle: MaybeRefOrGetter<string>) {
  const page = usePage();

  const packages = computed(() =>
    filterTrekRowsByTitle(
      page.value?.trekPackagesList as TrekPackageRow[] | undefined,
      toValue(trekTitle),
    ),
  );

  const prices1 = computed(() =>
    filterTrekRowsByTitle(
      page.value?.trekprices1 as TrekPriceRow[] | undefined,
      toValue(trekTitle),
    ),
  );

  const prices2 = computed(() =>
    filterTrekRowsByTitle(
      page.value?.trekprices2 as TrekPriceRow[] | undefined,
      toValue(trekTitle),
    ),
  );

  const summary = computed(() =>
    buildTrekPackageSummary({
      packages: packages.value,
      prices1: prices1.value,
      prices2: prices2.value,
      year1Label: page.value?.setYear1,
      year2Label: page.value?.setYear2,
    }),
  );

  function summaryForVariant(variant: TrekRouteVariant) {
    const pkg = matchPackageToRouteVariant(packages.value, variant);
    if (!pkg) return null;
    return buildTrekPackageSummary({
      packages: packages.value,
      preferredPackage: pkg,
      prices1: prices1.value,
      prices2: prices2.value,
      year1Label: page.value?.setYear1,
      year2Label: page.value?.setYear2,
    });
  }

  return {
    packages,
    prices1,
    prices2,
    summary,
    summaryForVariant,
  };
}
